// index.tsx (modified)
import AddNew from "./AddNew";
import List from "./List";
import { useGlobal } from "../../../context/global";
import { useEffect, useState } from "react";
import AdminService from "../../../services/admin.service";
import { Cost, Trip, TripCost } from "../../../types/common.types";
import Filter from "./Filter";
import { useAdmin } from "../../../context/admin";
import TripsList from "./TripsList";
import Accounting from "./Accounting";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

interface AccountingInterface {
  costs: TripCost[];
  grand_total: number;
}

export default function Costs() {
  const { currentDay, endDay } = useAdmin();
  const { setIsLoading } = useGlobal();
  const [selectedCost, setSelectedCost] = useState<string | undefined>();
  const [costs, setCosts] = useState<Cost[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [accounting, setAccounting] = useState<AccountingInterface>();

  useEffect(() => {
    async function getCost() {
      setIsLoading(true);
      try {
        const response = await (await AdminService.listCosts()).data;
        setCosts(response);
      } catch (error) {
        console.log(error);
      }
      try {
        if (selectedCost && currentDay) {
          const response = (
            await AdminService.getCostsByTripRange(selectedCost, currentDay, endDay)
          ).data;
          setTrips(response.trips);
          setAccounting(response.accounting);
        }
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    }
    getCost();
  }, [selectedCost, currentDay, endDay]);

const downloadPDFReport = () => {
  if (!accounting && trips.length === 0) return;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const maxTextWidth = pageWidth - 2 * margin;

  // Title
  const title = `Cost Report - ${selectedCost || "All Costs"}`;
  doc.setFontSize(18);
  doc.text(title, margin, 22);

  // Date range
  const fromDate = currentDay ? format(currentDay, "dd/MM/yyyy") : "N/A";
  const toDate = endDay ? format(endDay, "dd/MM/yyyy") : fromDate;
  doc.setFontSize(12);
  doc.text(`Period: ${fromDate} - ${toDate}`, margin, 32);

  let currentY = 45;

  // ----- Accounting summary table -----
  if (accounting) {
    doc.setFontSize(14);
    doc.text("Summary", margin, currentY);
    currentY += 6;

    const summaryData = accounting.costs.map((c) => [c.name, c.total.toFixed(2)]);
    summaryData.push(["GRAND TOTAL", accounting.grand_total.toFixed(2)]);

    autoTable(doc, {
      startY: currentY,
      head: [["Cost", "Total Value"]],
      body: summaryData,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 40, halign: "right" },
      },
      margin: { left: margin, right: margin },
    });
    currentY = (doc as any).lastAutoTable?.finalY + 12;
  }

  // ----- Trips details -----
  if (trips.length > 0) {
    doc.setFontSize(14);
    doc.text("Trips Details", margin, currentY);
    currentY += 12;

    for (let i = 0; i < trips.length; i++) {
      const trip = trips[i];

      // Build trip title
      const tripDate = trip.trip_date
        ? format(new Date(trip.trip_date), "dd/MM/yyyy")
        : "";
      const titleText = `${tripDate} - ${trip.trip_time} | ${trip.tour_id}`;

      // Wrap long title
      doc.setFontSize(11);
      const wrappedTitle = doc.splitTextToSize(titleText, maxTextWidth);
      const titleLines = wrappedTitle.length;
      const titleHeight = titleLines * 5; // ~5pt per line

      // Check space: need title + at least 2 rows of table (20pt) + small gap
      const minTableHeight = 20;
      const neededSpace = titleHeight + minTableHeight + 6;
      if (currentY + neededSpace > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        currentY = margin;
      }

      // Draw title
      doc.text(wrappedTitle, margin, currentY);
      currentY += titleHeight + 4; // tight gap before table

      // Prepare table data
      const costsData = trip.costs.map((c) => [c.name, c.value.toFixed(2)]);

      // Draw table
      autoTable(doc, {
        startY: currentY,
        head: [["Cost", "Value"]],
        body: costsData,
        theme: "plain",
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 40, halign: "right" },
        },
        margin: { left: margin, right: margin },
      });

      const tableFinalY = (doc as any).lastAutoTable?.finalY || currentY;

      // Add separator and gap before next trip (except after last)
      if (i < trips.length - 1) {
        // Light gray line to visually separate trips
        const lineY = tableFinalY + 6;
        if (lineY < doc.internal.pageSize.getHeight() - margin) {
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, lineY, pageWidth - margin, lineY);
          currentY = lineY + 8; // space after line
        } else {
          currentY = tableFinalY + 8;
        }
      } else {
        currentY = tableFinalY + 6;
      }

      // Page break for next iteration if needed
      if (currentY > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        currentY = margin;
      }
    }
  }

  // Save PDF
  doc.save(`cost_report_${selectedCost || "all"}_${fromDate}_${toDate}.pdf`);
};

  return (
    <>
      <Filter costs={costs} setSelectedCost={setSelectedCost} />
      {/* Download button */}
      {(accounting || trips.length > 0) && (
        <div style={{ textAlign: "center", margin: "10px 0" }}>
          <button
            onClick={downloadPDFReport}
            style={{
              padding: "8px 16px",
              backgroundColor: "#2c3e50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Download PDF Report
          </button>
        </div>
      )}
      <TripsList trips={trips} />
      {accounting && <Accounting accounting={accounting} />}
      <hr />
      <AddNew />
      <List list={costs} />
    </>
  );
}